import { __rest } from "tslib";
import * as React from 'react';
import { CoreInput } from '../internal/WixUiCoreComponents/components/CoreInput';
import { st, classes } from './TextField.st.css';
import { Check, Error, ChevronUpSmall, ChevronDownSmall, } from '@wix/wix-ui-icons-common/on-stage';
import { ClearXSmall } from '@wix/wix-ui-icons-common/on-stage/system';
import { IconButton, IconButtonSkins } from '../IconButton';
import { TooltipSkin } from '../Tooltip/types';
import { EMPTY, ERROR, SUCCESS, THEME, DATA_HOOKS } from './dataKeys';
import { IconTooltip } from '../IconTooltip';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { TextFieldTheme, } from './types';
import { getAriaDescribedBy } from '../../common/a11y';
import uniqueId from '../../common/uniqueId';
export class TextField extends React.Component {
    constructor(props) {
        var _a;
        super(props);
        this._rootRef = React.createRef();
        this.state = {
            isErrorTooltipShown: false,
        };
        this.TextFieldRef = React.createRef();
        this.focus = () => {
            this.TextFieldRef.current.focus();
        };
        this.blur = () => {
            this.TextFieldRef.current.blur();
        };
        this.showErrorTooltip = () => {
            this._toggleTooltip(true);
        };
        this.hideErrorTooltip = () => {
            this._toggleTooltip(false);
        };
        this._getClearButton = (shouldAddClearButtonGap) => {
            const { clearButtonAriaLabel, clearButtonAriaLabelledby, onClear } = this.props;
            return (React.createElement("div", { className: classes.clearButtonWrapper },
                React.createElement(IconButton, { className: classes.clearButton, "data-hook": DATA_HOOKS.CLEAR_BUTTON, "aria-label": clearButtonAriaLabel, "aria-labelledby": clearButtonAriaLabelledby, skin: IconButtonSkins.Line, onClick: onClear ? () => this.props.onClear() : undefined, icon: React.createElement(ClearXSmall, null) }),
                shouldAddClearButtonGap && React.createElement("div", { className: classes.clearButtonGap })));
        };
        this._getErrorSuffix = () => {
            const { isErrorTooltipShown } = this.state;
            const { errorMessage, errorTooltipPlacement, errorTooltipMaxWidth } = this.props;
            return (React.createElement(IconTooltip, { "data-hook": DATA_HOOKS.ERROR_ICON, message: errorMessage, icon: React.createElement(Error, { className: classes.errorIcon }), tooltipId: this._errorMessageId, tooltipSkin: TooltipSkin.Error, showTooltip: isErrorTooltipShown, onClick: this._onErrorButtonClick, onClickOutside: this._onClickOutside, tooltipProps: {
                    placement: errorTooltipPlacement,
                    moveBy: { x: 5, y: 0 },
                    maxWidth: errorTooltipMaxWidth,
                } }));
        };
        this._getSuccessSuffix = () => {
            return (React.createElement(Check, { "data-hook": DATA_HOOKS.SUCCESS_ICON, className: classes.successIcon }));
        };
        this._getArrowsSuffix = () => {
            const triggerInputEvent = () => { var _a; return (_a = this.inputRef) === null || _a === void 0 ? void 0 : _a.dispatchEvent(new Event('input', { bubbles: true })); };
            const stepUp = () => {
                var _a;
                (_a = this.inputRef) === null || _a === void 0 ? void 0 : _a.stepUp();
                triggerInputEvent();
            };
            const stepDown = () => {
                var _a;
                (_a = this.inputRef) === null || _a === void 0 ? void 0 : _a.stepDown();
                triggerInputEvent();
            };
            return (React.createElement("div", { className: classes.arrowsSuffixWrapper, "data-hook": DATA_HOOKS.ARROW_ICONS },
                React.createElement("button", { className: classes.arrowBtn, onClick: stepUp, type: "button" },
                    React.createElement(ChevronUpSmall, { size: 16 })),
                React.createElement("button", { className: classes.arrowBtn, onClick: stepDown, type: "button" },
                    React.createElement(ChevronDownSmall, { size: 16 }))));
        };
        this._getSuffix = () => {
            const { error, errorMessage, success, successIcon, suffix, withClearButton, value, disabled, newErrorMessage, type, } = this.props;
            const shouldShowCustomSuffix = !!suffix;
            const shouldShowErrorIcon = error && errorMessage && !newErrorMessage;
            const shouldShowSuccessIcon = successIcon && success;
            const shouldShowClearButton = withClearButton && value && !disabled;
            const shouldShowArrows = type === 'number';
            const shouldAddClearButtonGap = shouldShowCustomSuffix || shouldShowErrorIcon || shouldShowSuccessIcon;
            const hasSuffix = shouldShowCustomSuffix ||
                shouldShowErrorIcon ||
                shouldShowSuccessIcon ||
                shouldShowClearButton ||
                shouldShowArrows;
            return hasSuffix ? (React.createElement("div", { className: st(classes.suffixWrapper, {
                    arrows: shouldShowArrows,
                }) },
                shouldShowClearButton
                    ? this._getClearButton(shouldAddClearButtonGap)
                    : null,
                shouldShowErrorIcon ? this._getErrorSuffix() : null,
                shouldShowSuccessIcon ? this._getSuccessSuffix() : null,
                shouldShowArrows ? this._getArrowsSuffix() : null,
                suffix ? (React.createElement("div", { className: classes.customSuffix, "data-hook": DATA_HOOKS.CUSTOM_SUFFIX }, suffix)) : null)) : null;
        };
        this._getPrefix = () => {
            const { prefix, prefixLabel } = this.props;
            return prefix ? (React.createElement("span", { "data-hook": DATA_HOOKS.PREFIX, className: classes.prefix },
                prefix,
                prefixLabel ? (React.createElement("span", { id: this._prefixLabelId, className: classes.prefixLabel, "data-sr-only": "true", "data-hook": DATA_HOOKS.PREFIX_LABEL }, prefixLabel)) : null)) : null;
        };
        this._toggleTooltip = (showTooltip) => {
            this.setState({ isErrorTooltipShown: showTooltip });
        };
        this._onErrorButtonClick = () => {
            const { isErrorTooltipShown } = this.state;
            this._toggleTooltip(!isErrorTooltipShown);
        };
        this._onClickOutside = () => {
            if (this._rootRef.current &&
                !this._rootRef.current.contains(document.activeElement)) {
                this._toggleTooltip(false);
            }
        };
        this._onFocus = (e) => {
            const { onFocus } = this.props;
            if (onFocus) {
                onFocus(e);
            }
            if (!e.defaultPrevented &&
                this._rootRef.current &&
                this._rootRef.current.contains(document.activeElement)) {
                this._toggleTooltip(true);
            }
        };
        this._getCharCount = () => {
            const { value, maxLength } = this.props;
            return (React.createElement("div", { "data-hook": DATA_HOOKS.CHAR_COUNT, className: classes.charCount },
                React.createElement("div", { dir: "ltr" }, `${value.length}/${maxLength}`)));
        };
        this._inputId = (_a = props.id) !== null && _a !== void 0 ? _a : uniqueId('label-for-id');
        this._errorMessageId = uniqueId('error-message');
        this._prefixLabelId = uniqueId('prefix-label');
    }
    render() {
        const _a = this.props, { errorMessage, success, successIcon, error, disabled, theme, suffix, prefix, prefixLabel, className, withClearButton, onClear, clearButtonAriaLabel, clearButtonAriaLabelledby, errorTooltipPlacement, label, maxLength, showCharCount, value, forceLabel, inputClassName, 'aria-describedby': ariaDescribedByProp, newErrorMessage, inputRef } = _a, restProps = __rest(_a, ["errorMessage", "success", "successIcon", "error", "disabled", "theme", "suffix", "prefix", "prefixLabel", "className", "withClearButton", "onClear", "clearButtonAriaLabel", "clearButtonAriaLabelledby", "errorTooltipPlacement", "label", "maxLength", "showCharCount", "value", "forceLabel", "inputClassName", 'aria-describedby', "newErrorMessage", "inputRef"]);
        const dataObject = {
            [THEME]: theme,
            [SUCCESS]: success,
            [ERROR]: error,
            [EMPTY]: !this.props.value,
        };
        const Wrapper = newErrorMessage ? ErrorMessageWrapper : React.Fragment;
        const hasPrefix = prefix ? true : false;
        const hasErrorMessage = !!errorMessage;
        const stStates = {
            error,
            theme,
            success,
            disabled,
            hasPrefix,
            newErrorMessage,
            hasErrorMessage,
        };
        const ariaDescribedBy = getAriaDescribedBy([
            ariaDescribedByProp,
            errorMessage ? this._errorMessageId : '',
            prefixLabel ? this._prefixLabelId : '',
        ]);
        return (React.createElement(Wrapper, Object.assign({}, (newErrorMessage && {
            visible: error && !!errorMessage,
            message: errorMessage,
            messageId: this._errorMessageId,
            className: st(classes.root, stStates, className),
            suffixText: showCharCount && maxLength && this._getCharCount(),
        })),
            React.createElement("div", { className: newErrorMessage
                    ? st(classes.container)
                    : st(classes.root, stStates, className), ref: this._rootRef },
                label ? (React.createElement("label", { "data-hook": DATA_HOOKS.LABEL, htmlFor: this._inputId, className: classes.label }, label)) : (forceLabel && (React.createElement("div", { className: classes.label, "aria-hidden": "true", "data-hook": DATA_HOOKS.LABEL_PLACEHOLDER }, "\u00A0"))),
                React.createElement(CoreInput, Object.assign({}, dataObject, { className: st(classes.input, {
                        theme,
                    }), inputClassName: `${inputClassName || ''} has-custom-focus`, id: this._inputId, "aria-describedby": ariaDescribedBy, value: value, maxLength: maxLength, ref: this.TextFieldRef, suffix: this._getSuffix(), prefix: this._getPrefix(), error: error, "aria-invalid": error }, restProps, { disabled: disabled, onFocus: this._onFocus, inputRef: (input) => {
                        this.inputRef = input;
                        inputRef === null || inputRef === void 0 ? void 0 : inputRef(input);
                    } })),
                maxLength &&
                    !(newErrorMessage && error && errorMessage) &&
                    showCharCount &&
                    this._getCharCount())));
    }
}
TextField.displayName = 'TextField';
TextField.defaultProps = {
    success: false,
    successIcon: false,
    error: false,
    withClearButton: false,
    disabled: false,
    theme: TextFieldTheme.Box,
    errorTooltipPlacement: 'top-end',
    forceLabel: false,
};
//# sourceMappingURL=TextField.js.map